/**
 * Service pour gérer les données du Dashboard avec des données réelles
 */

import { devisApiService } from './DevisApiService';
import { clientService, Client } from './ClientService';
import { Devis } from './types';

// Types pour les statistiques du Dashboard
export interface DashboardStats {
  totalDevis: number;
  devisAcceptes: number;
  devisEnAttente: number;
  devisRefuses: number;
  caTotal: number;
  tauxConversion: number;
  montantMoyen: number;
  totalClients: number;
}

// Types pour les graphiques
export interface StatutsRepartition {
  labels: string[];
  data: number[];
  colors: string[];
}

export interface TimelineData {
  labels: string[];
  data: number[];
}

// Types pour l'aide commerciale
export interface ActionCommerciale {
  type: 'relance' | 'livraison' | 'finalisation';
  devisId: string;
  reference: string;
  clientNom: string;
  montant: number;
  joursAttente: number;
  urgence: 'faible' | 'moyenne' | 'haute';
}

// Types pour les devis récents avec informations client
export interface DevisRecentAvecClient {
  id: string;
  reference: string;
  clientNom: string;
  clientSociete?: string;
  date: string;
  montant: number;
  statut: string; // Utiliser les vrais statuts du backend
}


class DashboardService {
  /**
   * Détermine le statut d'un devis basé sur ses propriétés
   */
  private determinerStatutDevis(devis: Devis): 'accepté' | 'en_attente' | 'refusé' | 'brouillon' {
    // Si le devis a une propriété statut explicite, l'utiliser
    if ((devis as any).statut) {
      return (devis as any).statut;
    }
    
    // Sinon, déterminer le statut basé sur d'autres critères
    // Par exemple, si le devis a été créé récemment, il est probablement en attente
    const dateDevis = new Date(devis.date);
    const maintenant = new Date();
    const diffJours = Math.floor((maintenant.getTime() - dateDevis.getTime()) / (1000 * 60 * 60 * 24));
    
    // Logique simple : devis récents (< 7 jours) = en_attente, anciens = accepté
    if (diffJours < 7) {
      return 'en_attente';
    } else {
      return 'accepté';
    }
  }

  /**
   * Récupère les statistiques du Dashboard avec des données réelles
   */
  async getStatistiques(): Promise<DashboardStats> {
    try {
      // Récupérer les données en parallèle
      const [devisList, clientsList] = await Promise.all([
        devisApiService.getDevis(),
        clientService.getClients()
      ]);

      // Calculer les statistiques
      const totalDevis = devisList.length;
      let devisAcceptes = 0;
      let devisEnAttente = 0;
      let devisRefuses = 0;
      let caTotal = 0;
      let montantTotal = 0;

      devisList.forEach(devis => {
        const statut = this.determinerStatutDevis(devis);
        const montant = devis.montantTotal || 0;
        
        switch (statut) {
          case 'accepté':
            devisAcceptes++;
            caTotal += montant;
            break;
          case 'en_attente':
            devisEnAttente++;
            break;
          case 'refusé':
            devisRefuses++;
            break;
        }
        
        montantTotal += montant;
      });

      const tauxConversion = totalDevis > 0 ? Math.round((devisAcceptes / totalDevis) * 100) : 0;
      const montantMoyen = totalDevis > 0 ? Math.round(montantTotal / totalDevis) : 0;

      return {
        totalDevis,
        devisAcceptes,
        devisEnAttente,
        devisRefuses,
        caTotal: Math.round(caTotal),
        tauxConversion,
        montantMoyen,
        totalClients: clientsList.length
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      
      // Retourner des statistiques par défaut en cas d'erreur
      return {
        totalDevis: 0,
        devisAcceptes: 0,
        devisEnAttente: 0,
        devisRefuses: 0,
        caTotal: 0,
        tauxConversion: 0,
        montantMoyen: 0,
        totalClients: 0
      };
    }
  }

  /**
   * Récupère les devis récents avec les informations client
   */
  async getDevisRecents(limite: number = 5): Promise<DevisRecentAvecClient[]> {
    try {
      // Récupérer les données en parallèle
      const [devisList, clientsList] = await Promise.all([
        devisApiService.getDevis(),
        clientService.getClients()
      ]);

      // Créer un map des clients pour un accès rapide
      const clientsMap = new Map<string, Client>();
      clientsList.forEach(client => {
        if (client.id) {
          clientsMap.set(client.id, client);
        }
      });

      // Trier les devis par date (plus récents en premier)
      const devisTries = devisList
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limite);

      // Enrichir avec les informations client
      const devisAvecClient: DevisRecentAvecClient[] = devisTries.map(devis => {
        const client = clientsMap.get(devis.client);
        // Utiliser directement le statut stocké en backend
        const statut = (devis as any).statut || 'NOUVEAU';
        
        return {
          id: devis.id || '',
          reference: devis.reference || `DEV-${devis.id}`,
          clientNom: client ? `${client.prenom} ${client.nom}` : 'Client inconnu',
          clientSociete: client?.societe,
          date: this.formaterDate(devis.date),
          montant: devis.montantTotal || 0,
          statut
        };
      });

      return devisAvecClient;
    } catch (error) {
      console.error('Erreur lors de la récupération des devis récents:', error);
      return [];
    }
  }


  /**
   * Récupère les alertes (devis en attente depuis longtemps)
   */
  async getAlertes(): Promise<string[]> {
    try {
      const devisList = await devisApiService.getDevis();
      const alertes: string[] = [];
      const maintenant = new Date();

      devisList.forEach(devis => {
        const statut = this.determinerStatutDevis(devis);
        if (statut === 'en_attente') {
          const dateDevis = new Date(devis.date);
          const diffJours = Math.floor((maintenant.getTime() - dateDevis.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffJours > 7) {
            alertes.push(`Devis ${devis.reference || devis.id} en attente depuis ${diffJours} jours`);
          }
        }
      });

      return alertes;
    } catch (error) {
      console.error('Erreur lors de la récupération des alertes:', error);
      return [];
    }
  }

  /**
   * Formate une date au format français
   */
  private formaterDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  }

  /**
   * Récupère la répartition des statuts pour le graphique en secteurs
   */
  async getStatutsRepartition(): Promise<StatutsRepartition> {
    try {
      const devisList = await devisApiService.getDevis();
      const statutsCount: Record<string, number> = {};
      
      // Compter les statuts
      devisList.forEach(devis => {
        const statut = (devis as any).statut || 'NOUVEAU';
        statutsCount[statut] = (statutsCount[statut] || 0) + 1;
      });

      // Préparer les données pour Chart.js
      const labels: string[] = [];
      const data: number[] = [];
      const colors: string[] = [];
      
      const statutsConfig = {
        'NOUVEAU': { label: '📄 Nouveau', color: '#9CA3AF' },
        'BROUILLON': { label: '📝 Brouillon', color: '#6B7280' },
        'ENREGISTRE': { label: '💾 Enregistré', color: '#3B82F6' },
        'COMPLET': { label: '✅ Complet', color: '#10B981' },
        'EN_ATTENTE_RETOUR_CLIENT': { label: '⏳ En attente', color: '#F59E0B' },
        'ACCEPTE': { label: '✍️ Accepté', color: '#2563EB' },
        'TERMINE_LIVRE': { label: '🎉 Livré', color: '#8B5CF6' },
        'ANNULE': { label: '❌ Annulé', color: '#EF4444' },
        'ABANDONNE': { label: '🚫 Abandonné', color: '#F59E0B' }
      };

      Object.entries(statutsCount).forEach(([statut, count]) => {
        const config = statutsConfig[statut as keyof typeof statutsConfig] || { label: statut, color: '#9CA3AF' };
        labels.push(config.label);
        data.push(count);
        colors.push(config.color);
      });

      return { labels, data, colors };
    } catch (error) {
      console.error('Erreur lors de la récupération de la répartition des statuts:', error);
      return { labels: [], data: [], colors: [] };
    }
  }

  /**
   * Récupère les actions commerciales à effectuer
   */
  async getActionsCommerciales(): Promise<ActionCommerciale[]> {
    try {
      const [devisList, clientsList] = await Promise.all([
        devisApiService.getDevis(),
        clientService.getClients()
      ]);

      const clientsMap = new Map<string, Client>();
      clientsList.forEach(client => {
        if (client.id) {
          clientsMap.set(client.id, client);
        }
      });

      const actions: ActionCommerciale[] = [];
      const maintenant = new Date();

      devisList.forEach(devis => {
        const statut = (devis as any).statut || 'NOUVEAU';
        const dateDevis = new Date(devis.date);
        const joursAttente = Math.floor((maintenant.getTime() - dateDevis.getTime()) / (1000 * 60 * 60 * 24));
        const client = clientsMap.get(devis.client);
        const clientNom = client ? `${client.prenom} ${client.nom}` : 'Client inconnu';

        // Devis à relancer (en attente depuis plus de 7 jours)
        if (statut === 'EN_ATTENTE_RETOUR_CLIENT' && joursAttente > 7) {
          actions.push({
            type: 'relance',
            devisId: devis.id || '',
            reference: devis.reference || '',
            clientNom,
            montant: devis.montantTotal || 0,
            joursAttente,
            urgence: joursAttente > 14 ? 'haute' : joursAttente > 10 ? 'moyenne' : 'faible'
          });
        }

        // Devis acceptés à livrer
        if (statut === 'ACCEPTE') {
          actions.push({
            type: 'livraison',
            devisId: devis.id || '',
            reference: devis.reference || '',
            clientNom,
            montant: devis.montantTotal || 0,
            joursAttente,
            urgence: joursAttente > 7 ? 'haute' : 'moyenne'
          });
        }

        // Nouveaux devis à finaliser (plus de 3 jours)
        if ((statut === 'NOUVEAU' || statut === 'BROUILLON') && joursAttente > 3) {
          actions.push({
            type: 'finalisation',
            devisId: devis.id || '',
            reference: devis.reference || '',
            clientNom,
            montant: devis.montantTotal || 0,
            joursAttente,
            urgence: joursAttente > 7 ? 'moyenne' : 'faible'
          });
        }
      });

      // Trier par urgence puis par nombre de jours
      return actions.sort((a, b) => {
        const urgenceOrder = { 'haute': 3, 'moyenne': 2, 'faible': 1 };
        if (urgenceOrder[a.urgence] !== urgenceOrder[b.urgence]) {
          return urgenceOrder[b.urgence] - urgenceOrder[a.urgence];
        }
        return b.joursAttente - a.joursAttente;
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des actions commerciales:', error);
      return [];
    }
  }

  /**
   * Récupère les données complètes du Dashboard
   */
  async getDonneesDashboard() {
    try {
      const [statistiques, devisRecents, alertes, statutsRepartition, actionsCommerciales] = await Promise.all([
        this.getStatistiques(),
        this.getDevisRecents(5),
        this.getAlertes(),
        this.getStatutsRepartition(),
        this.getActionsCommerciales()
      ]);

      return {
        statistiques,
        devisRecents,
        alertes,
        statutsRepartition,
        actionsCommerciales
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des données du Dashboard:', error);
      throw error;
    }
  }
}

// Exporter une instance unique du service
export const dashboardService = new DashboardService();
